import React from 'react';

/**
 * Shared flyer preview — used by both /admin/flyer (editor) and /flyer (public).
 * Sized to US letter (850×1100 @ scale=2 → 1700×2200 export).
 * Everything is inline-styled so html2canvas exports it verbatim.
 */
const LOGO_URL = '/casa-samba-logo.png';

const FlyerPreview = React.forwardRef(function FlyerPreview(
  { heroImage, galleryImages = [], classes = [] },
  ref
) {
  return (
    <div
      ref={ref}
      data-testid="flyer-preview"
      className="relative shadow-2xl overflow-hidden mx-auto"
      style={{
        width: 850,
        height: 1100,
        background: '#0a0a0a',
        color: '#fff',
        fontFamily: 'Georgia, serif',
      }}
    >
      {/* Diagonal gold stripes background */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'repeating-linear-gradient(135deg, transparent 0, transparent 24px, rgba(244,180,0,0.04) 24px, rgba(244,180,0,0.04) 26px)',
          pointerEvents: 'none',
        }}
      />

      {/* Hero */}
      <div
        style={{
          position: 'relative',
          height: 420,
          overflow: 'hidden',
          background: '#000',
        }}
      >
        {heroImage && (
          <img
            src={heroImage}
            alt="Casa Samba"
            crossOrigin="anonymous"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              opacity: 0.75,
            }}
          />
        )}
        {/* Corner ribbon */}
        <div
          style={{
            position: 'absolute',
            top: 22,
            right: -60,
            transform: 'rotate(45deg)',
            background: '#dc2626',
            color: '#fff',
            padding: '8px 80px',
            fontWeight: 900,
            letterSpacing: 3,
            fontSize: 14,
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          }}
        >
          38 YEARS
        </div>

        {/* Overlay gradient */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(10,10,10,0.15) 0%, rgba(10,10,10,0.55) 55%, rgba(10,10,10,0.95) 100%)',
          }}
        />

        {/* Hero text */}
        <div
          style={{
            position: 'absolute',
            left: 40,
            right: 40,
            bottom: 28,
          }}
        >
          <div
            style={{
              display: 'inline-block',
              background: '#f4b400',
              color: '#000',
              padding: '6px 14px',
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 900,
              letterSpacing: 2.5,
              textTransform: 'uppercase',
              marginBottom: 14,
              boxShadow: '0 6px 14px rgba(244,180,0,0.35)',
            }}
          >
            Escola de Samba · Casa Samba · Since 1987
          </div>
          <div
            style={{
              fontSize: 72,
              lineHeight: 0.95,
              fontWeight: 900,
              letterSpacing: -2,
              textShadow: '0 6px 24px rgba(0,0,0,0.7)',
            }}
          >
            AFRO-BRAZILIAN
          </div>
          <div
            style={{
              fontSize: 42,
              lineHeight: 1.05,
              fontWeight: 700,
              marginTop: 4,
              color: '#f4b400',
              letterSpacing: 1,
            }}
          >
            Culture · Dance · Drum
          </div>
        </div>
      </div>

      {/* Styles we teach — pill row */}
      <div
        style={{
          background: '#0a0a0a',
          borderBottom: '2px solid rgba(244,180,0,0.35)',
          padding: '14px 40px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {[
          'Samba',
          'Afro-Brasilian',
          'Orixa',
          'Capoeira',
          'Coco',
          'Maracatu',
          'Stilt',
        ].map((s) => (
          <span
            key={s}
            style={{
              display: 'inline-block',
              padding: '5px 12px',
              borderRadius: 999,
              border: '1.5px solid #f4b400',
              color: '#f4b400',
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            {s}
          </span>
        ))}
      </div>

      {/* Price band */}
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          background: 'linear-gradient(90deg, #f4b400 0%, #ffcf3a 50%, #f4b400 100%)',
          color: '#000',
          borderTop: '3px solid #000',
          borderBottom: '3px solid #000',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <div
          style={{
            flex: 1,
            padding: '20px 30px',
            textAlign: 'center',
            borderRight: '3px solid #000',
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: 3, fontWeight: 800 }}>PER CLASS</div>
          <div style={{ fontSize: 54, fontWeight: 900, lineHeight: 1, marginTop: 2 }}>$10</div>
        </div>
        <div style={{ flex: 1, padding: '20px 30px', textAlign: 'center', borderRight: '3px solid #000' }}>
          <div style={{ fontSize: 11, letterSpacing: 3, fontWeight: 800 }}>FIRST CLASS</div>
          <div style={{ fontSize: 34, fontWeight: 900, lineHeight: 1, marginTop: 6, color: '#dc2626' }}>
            FREE!
          </div>
        </div>
        <div style={{ flex: 1, padding: '20px 30px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, letterSpacing: 3, fontWeight: 800 }}>WALK-INS</div>
          <div style={{ fontSize: 24, fontWeight: 900, lineHeight: 1.05, marginTop: 8 }}>
            WELCOME
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '26px 40px', position: 'relative', zIndex: 2 }}>
        {/* Schedule */}
        <div style={{ marginBottom: 22 }}>
          <div
            style={{
              fontSize: 13,
              letterSpacing: 4,
              fontWeight: 900,
              color: '#f4b400',
              marginBottom: 10,
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 40,
                height: 3,
                background: '#f4b400',
              }}
            />
            Regular Class Schedule
            <span
              style={{
                display: 'inline-block',
                flex: 1,
                height: 3,
                background: 'rgba(244,180,0,0.3)',
              }}
            />
          </div>
          {classes.length === 0 ? (
            <div style={{ fontSize: 16, color: 'rgba(255,255,255,0.6)' }}>
              Contact us for the current weekly schedule.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {classes.slice(0, 4).map((c) => (
                <div
                  key={c.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '11px 16px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(244,180,0,0.35)',
                    borderRadius: 10,
                    borderLeft: '5px solid #f4b400',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                      {(c.level || '').replace(/^./, (m) => m.toUpperCase())} · {c.category === 'kids' ? 'Kids' : 'Adults'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', color: '#f4b400', fontWeight: 800 }}>
                    <div style={{ fontSize: 16 }}>{c.day_of_week || ''}</div>
                    <div style={{ fontSize: 12 }}>
                      {c.start_time || ''}{c.end_time ? ` – ${c.end_time}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Private classes callout */}
        <div
          style={{
            background: 'linear-gradient(90deg, #b91c1c 0%, #f4b400 100%)',
            color: '#fff',
            padding: '14px 22px',
            borderRadius: 12,
            marginBottom: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 20,
            boxShadow: '0 8px 24px rgba(220,38,38,0.35)',
          }}
        >
          <div style={{ fontSize: 22, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 1 }}>
            🥁 Private Classes Available
          </div>
          <div style={{ fontSize: 13, opacity: 0.95, fontWeight: 700 }}>
            By appointment · Call to book
          </div>
        </div>

        {/* Accent gallery */}
        {galleryImages.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${galleryImages.length}, 1fr)`,
              gap: 8,
              marginBottom: 18,
            }}
          >
            {galleryImages.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`Casa Samba ${i + 1}`}
                crossOrigin="anonymous"
                style={{
                  width: '100%',
                  height: 120,
                  objectFit: 'cover',
                  borderRadius: 8,
                  border: '3px solid #f4b400',
                }}
              />
            ))}
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            borderTop: '2px solid rgba(244,180,0,0.35)',
            paddingTop: 16,
          }}
        >
          <img
            src={LOGO_URL}
            alt="Casa Samba"
            crossOrigin="anonymous"
            style={{ width: 72, height: 72, objectFit: 'contain' }}
          />
          <div style={{ flex: 1, fontSize: 14, lineHeight: 1.5 }}>
            <div style={{ fontWeight: 900, fontSize: 20, color: '#f4b400' }}>
              Mestre Curtis Pierre
            </div>
            <div style={{ opacity: 0.85 }}>Escola de Samba Casa Samba</div>
            <div style={{ opacity: 0.75, fontSize: 13 }}>
              N.O.R.D. Annunciation Center · 800 Race St, New Orleans, LA 70130
            </div>
            <div style={{ marginTop: 4, color: '#f4b400', fontWeight: 800, fontSize: 16 }}>
              📞 504-236-7479 &nbsp;·&nbsp; 🌐 casasamba.com
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default FlyerPreview;
